import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { Prospect } from '../types/api';

export const prospectsKeys = {
  all: ['prospects'] as const,
  list: (projectId: string | null, filters: ListFilters) =>
    [...prospectsKeys.all, 'list', projectId, filters] as const,
  byId: (projectId: string | null, id: string) =>
    [...prospectsKeys.all, 'byId', projectId, id] as const,
};

export interface ListFilters {
  q?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useProspectsList(filters: ListFilters = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: prospectsKeys.list(projectId, filters),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ prospects: Prospect[] }>('/prospects', { ...filters });
      return res.prospects;
    },
  });
}

export function useProspect(id: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: id ? prospectsKeys.byId(projectId, id) : ['prospects', 'none'],
    enabled: !!id && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ prospect: Prospect }>(`/prospects/${id}`);
      return res.prospect;
    },
  });
}

export interface ProspectInput {
  name: string;
  description?: string | null | undefined;
}

export function useCreateProspect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProspectInput) => api.post<{ prospect: Prospect }>('/prospects', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: prospectsKeys.all }),
  });
}

export function useUpdateProspect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<ProspectInput> }) =>
      api.patch<{ prospect: Prospect }>(`/prospects/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: prospectsKeys.all }),
  });
}

export function useDeleteProspect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/prospects/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: prospectsKeys.all }),
  });
}
